import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users as UsersIcon, Search, RefreshCw, Filter,
  Mail, Phone, Shield, Calendar, Edit2, Power, Eye,
  ChevronLeft, ChevronRight, Download,
  Wallet, CheckCircle, XCircle, X, FileText,
  Tag, Plus, Globe, FlaskConical, Activity, Award,
  Clock, Trash2, BadgeCheck, AlertCircle,
} from 'lucide-react';
import { adminService } from '../services/adminService';
import type { AdminUserListItem, UserNote, UserTimelineEvent } from '../types';
import { formatDistanceToNow, format } from 'date-fns';
import { exportToCSV, exportToPDF, formatDateForExport } from '../utils/exportUtils';
import { useToast } from '../contexts/ToastContext';
import { formatCompactNumber, formatNumber } from '../utils/format';
import './Users.css';

interface EditUserData {
  full_name: string;
  email: string;
  phone_number: string;
  password: string;
}

const KYC_TIERS = ['tier_0', 'tier_1', 'tier_2', 'tier_3'];

const Users: React.FC = () => {
  const [users, setUsers] = useState<AdminUserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<boolean | null>(null);
  const [tagFilter, setTagFilter] = useState('');
  const [kycFilter, setKycFilter] = useState('');
  const [acquisitionFilter, setAcquisitionFilter] = useState('');
  const [calibrationFilter, setCalibrationFilter] = useState<'' | 'only' | 'exclude'>('');
  const [testFilter, setTestFilter] = useState<'' | 'only' | 'exclude'>('exclude');
  const [activeWithinDays, setActiveWithinDays] = useState<number | ''>('');
  const [sort, setSort] = useState('created_at_desc');

  const [editingUser, setEditingUser] = useState<AdminUserListItem | null>(null);
  const [viewingUser, setViewingUser] = useState<AdminUserListItem | null>(null);
  const [timeline, setTimeline] = useState<UserTimelineEvent[]>([]);
  const [notes, setNotes] = useState<UserNote[]>([]);
  const [newNote, setNewNote] = useState('');
  const [newTag, setNewTag] = useState('');

  const [editForm, setEditForm] = useState<EditUserData>({
    full_name: '',
    email: '',
    phone_number: '',
    password: '',
  });
  const [page, setPage] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkTag, setBulkTag] = useState('');
  const pageSize = 20;
  const toast = useToast();

  const userExportColumns = [
    { key: 'id', header: 'ID' },
    { key: 'full_name', header: 'Full Name' },
    { key: 'email', header: 'Email' },
    { key: 'phone_number', header: 'Phone' },
    { key: 'role', header: 'Role' },
    { key: 'country_code', header: 'Country' },
    { key: 'kyc_tier', header: 'KYC' },
    { key: 'tags', header: 'Tags', formatter: (v?: string[]) => (v && v.length ? v.join(', ') : '') },
    { key: 'is_active', header: 'Status', formatter: (v: boolean) => v ? 'Active' : 'Inactive' },
    { key: 'last_active_at', header: 'Last active', formatter: formatDateForExport },
    { key: 'lifetime_sessions', header: 'Sessions' },
    { key: 'lifetime_earnings_tp', header: 'Lifetime TP' },
    { key: 'created_at', header: 'Joined', formatter: formatDateForExport },
  ];

  const handleExportCSV = () => {
    exportToCSV(users, userExportColumns, 'users_export');
    toast.success('Export Complete', 'Users exported to CSV');
    setShowExportMenu(false);
  };

  const handleExportPDF = () => {
    exportToPDF(users, userExportColumns, 'User Management Report', 'users_report');
    toast.success('Export Complete', 'PDF report generated');
    setShowExportMenu(false);
  };

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        search: search || undefined,
        role: roleFilter || undefined,
        is_active: statusFilter !== null ? statusFilter : undefined,
        tag: tagFilter || undefined,
        kyc_tier: kycFilter || undefined,
        acquisition_source: acquisitionFilter || undefined,
        last_active_within_days: activeWithinDays || undefined,
        sort,
        skip: page * pageSize,
        limit: pageSize,
      };
      if (calibrationFilter === 'only') params.is_calibration_user = true;
      if (calibrationFilter === 'exclude') params.is_calibration_user = false;
      if (testFilter === 'only') params.is_test_account = true;
      if (testFilter === 'exclude') params.is_test_account = false;

      const data = await adminService.listUsersRich(params);
      setUsers(data?.items || []);
      setTotalUsers(data?.total || 0);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  }, [search, roleFilter, statusFilter, tagFilter, kycFilter, acquisitionFilter,
      calibrationFilter, testFilter, activeWithinDays, sort, page]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      loadUsers();
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [loadUsers]);

  const handleRoleChange = async (userId: number, newRole: string) => {
    if (!window.confirm(`Are you sure you want to change this user's role to ${newRole}?`)) return;
    try {
      await adminService.updateUserRole(userId, newRole);
      await loadUsers();
    } catch (error) {
      alert('Failed to update user role');
    }
  };

  const handleStatusChange = async (userId: number, isActive: boolean) => {
    const action = isActive ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;
    try {
      await adminService.updateUserStatus(userId, isActive);
      await loadUsers();
    } catch (error) {
      alert('Failed to update user status');
    }
  };

  const handleEditClick = (user: AdminUserListItem) => {
    setEditingUser(user);
    setEditForm({
      full_name: user.full_name,
      email: user.email,
      phone_number: user.phone_number,
      password: '',
    });
  };

  const handleViewClick = async (user: AdminUserListItem) => {
    setViewingUser(user);
    setTimeline([]);
    setNotes([]);
    setNewNote('');
    setNewTag('');
    try {
      const [tl, ns] = await Promise.allSettled([
        adminService.getUserTimeline(user.id),
        adminService.listUserNotes(user.id),
      ]);
      if (tl.status === 'fulfilled') setTimeline(tl.value?.events || []);
      if (ns.status === 'fulfilled') setNotes(ns.value || []);
    } catch (e) {
      console.warn('Failed to load timeline/notes', e);
    }
  };

  const handleEditCancel = () => {
    setEditingUser(null);
    setEditForm({ full_name: '', email: '', phone_number: '', password: '' });
  };

  const handleViewClose = () => {
    setViewingUser(null);
    setTimeline([]);
    setNotes([]);
  };

  const handleEditSave = async () => {
    if (!editingUser) return;
    try {
      const updateData: any = {};
      if (editForm.full_name !== editingUser.full_name) updateData.full_name = editForm.full_name;
      if (editForm.email !== editingUser.email) updateData.email = editForm.email;
      if (editForm.phone_number !== editingUser.phone_number) updateData.phone_number = editForm.phone_number;
      if (editForm.password && editForm.password.length >= 6) updateData.password = editForm.password;

      if (Object.keys(updateData).length === 0) {
        alert('No changes to save');
        return;
      }
      await adminService.updateUser(editingUser.id, updateData);
      await loadUsers();
      handleEditCancel();
      toast.success('Saved', 'User updated successfully');
    } catch (error: any) {
      alert(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleAddTag = async (userId: number, tag: string) => {
    if (!tag.trim()) return;
    try {
      const res = await adminService.addUserTag(userId, tag.trim());
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, tags: res.tags } : u));
      if (viewingUser?.id === userId) setViewingUser({ ...viewingUser, tags: res.tags });
      setNewTag('');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to add tag');
    }
  };

  const handleRemoveTag = async (userId: number, tag: string) => {
    try {
      const res = await adminService.removeUserTag(userId, tag);
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, tags: res.tags } : u));
      if (viewingUser?.id === userId) setViewingUser({ ...viewingUser, tags: res.tags });
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to remove tag');
    }
  };

  const handleSetKyc = async (userId: number, tier: string) => {
    try {
      const res = await adminService.setUserKycTier(userId, tier);
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, kyc_tier: res.kyc_tier, kyc_verified_at: res.kyc_verified_at } : u));
      if (viewingUser?.id === userId) setViewingUser({ ...viewingUser, kyc_tier: res.kyc_tier, kyc_verified_at: res.kyc_verified_at });
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to update KYC tier');
    }
  };

  const handleToggleTest = async (userId: number, isTest: boolean) => {
    try {
      const res = await adminService.setUserTestFlag(userId, isTest);
      setUsers((prev) => prev.map((u) => u.id === userId ? { ...u, is_test_account: res.is_test_account } : u));
      if (viewingUser?.id === userId) setViewingUser({ ...viewingUser, is_test_account: res.is_test_account });
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to update test flag');
    }
  };

  const handleAddNote = async () => {
    if (!viewingUser || !newNote.trim()) return;
    try {
      const res = await adminService.createUserNote(viewingUser.id, newNote.trim());
      setNotes((prev) => [res, ...prev]);
      setNewNote('');
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Failed to add note');
    }
  };

  const handleBulkTag = async (action: 'add' | 'remove') => {
    if (!bulkTag.trim() || selectedIds.size === 0) return;
    if (!window.confirm(`${action === 'add' ? 'Add' : 'Remove'} tag "${bulkTag}" ${action === 'add' ? 'to' : 'from'} ${selectedIds.size} users?`)) return;
    try {
      await Promise.allSettled(Array.from(selectedIds).map((id) => action === 'add'
        ? adminService.addUserTag(id, bulkTag.trim())
        : adminService.removeUserTag(id, bulkTag.trim())
      ));
      toast.success('Bulk update', `${action === 'add' ? 'Tagged' : 'Untagged'} ${selectedIds.size} users`);
      setSelectedIds(new Set());
      setBulkTag('');
      loadUsers();
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Bulk update failed');
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllOnPage = () => {
    setSelectedIds((prev) => {
      const all = users.every((u) => prev.has(u.id));
      const next = new Set(prev);
      users.forEach((u) => all ? next.delete(u.id) : next.add(u.id));
      return next;
    });
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return '#5a1494';
      case 'moderator': return '#f59e0b';
      default: return '#410C6D';
    }
  };

  const allOnPageSelected = users.length > 0 && users.every((u) => selectedIds.has(u.id));
  const someSelected = selectedIds.size > 0;

  const visibleSummary = useMemo(() => ({
    total: totalUsers,
    activeOnPage: users.filter((u) => u.is_active).length,
    adminsOnPage: users.filter((u) => u.role === 'admin').length,
    calibrationOnPage: users.filter((u) => u.is_calibration_user).length,
    testOnPage: users.filter((u) => u.is_test_account).length,
  }), [totalUsers, users]);

  return (
    <div className="users-page-modern">
      {/* Header */}
      <div className="users-header">
        <div className="header-content">
          <h1><UsersIcon size={28} /> User Management</h1>
          <p className="header-subtitle">Manage user accounts, tags, KYC, and pre-launch storytelling</p>
        </div>
        <div className="header-actions">
          <button onClick={loadUsers} className="refresh-btn" disabled={loading}>
            <RefreshCw className={loading ? 'spin' : ''} size={18} />
            Refresh
          </button>
          <div className="export-dropdown">
            <button
              className="export-btn"
              onClick={() => setShowExportMenu(!showExportMenu)}
            >
              <Download size={18} />
              Export
            </button>
            {showExportMenu && (
              <div className="export-menu">
                <button onClick={handleExportCSV}>
                  <FileText size={16} /> Export as CSV
                </button>
                <button onClick={handleExportPDF}>
                  <FileText size={16} /> Export as PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          />
          {search && (
            <button className="clear-search" onClick={() => { setSearch(''); setPage(0); }}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="filter-group">
          <Filter size={16} />
          <select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}>
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="user">User</option>
          </select>
          <select value={statusFilter === null ? '' : statusFilter.toString()}
            onChange={(e) => {
              const val = e.target.value;
              setStatusFilter(val === '' ? null : val === 'true');
              setPage(0);
            }}>
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </select>
          <select value={kycFilter} onChange={(e) => { setKycFilter(e.target.value); setPage(0); }}>
            <option value="">All KYC tiers</option>
            {KYC_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={calibrationFilter} onChange={(e) => { setCalibrationFilter(e.target.value as any); setPage(0); }}>
            <option value="">All cohorts</option>
            <option value="only">Calibration only</option>
            <option value="exclude">Production only</option>
          </select>
          <select value={testFilter} onChange={(e) => { setTestFilter(e.target.value as any); setPage(0); }}>
            <option value="exclude">Exclude test accounts</option>
            <option value="">Include test accounts</option>
            <option value="only">Test accounts only</option>
          </select>
          <input
            type="text"
            placeholder="Tag…"
            value={tagFilter}
            onChange={(e) => { setTagFilter(e.target.value); setPage(0); }}
            style={{ maxWidth: 110 }}
          />
          <input
            type="text"
            placeholder="Acquisition source…"
            value={acquisitionFilter}
            onChange={(e) => { setAcquisitionFilter(e.target.value); setPage(0); }}
            style={{ maxWidth: 150 }}
          />
          <select value={activeWithinDays} onChange={(e) => { setActiveWithinDays(e.target.value === '' ? '' : Number(e.target.value)); setPage(0); }}>
            <option value="">Active anytime</option>
            <option value={1}>Active 1d</option>
            <option value={7}>Active 7d</option>
            <option value={30}>Active 30d</option>
            <option value={90}>Active 90d</option>
          </select>
          <select value={sort} onChange={(e) => { setSort(e.target.value); setPage(0); }}>
            <option value="created_at_desc">Newest</option>
            <option value="created_at_asc">Oldest</option>
            <option value="last_active_desc">Last active</option>
            <option value="lifetime_earnings_desc">Top earners</option>
            <option value="lifetime_payouts_desc">Top payouts</option>
          </select>
        </div>
      </div>

      {/* Stats Summary */}
      <div className="users-summary">
        <div className="summary-item">
          <span className="summary-value">{visibleSummary.total}</span>
          <span className="summary-label">Total (filtered)</span>
        </div>
        <div className="summary-item">
          <span className="summary-value">{visibleSummary.activeOnPage}</span>
          <span className="summary-label">Active on page</span>
        </div>
        <div className="summary-item">
          <span className="summary-value">{visibleSummary.adminsOnPage}</span>
          <span className="summary-label">Admins</span>
        </div>
        <div className="summary-item">
          <span className="summary-value">{visibleSummary.calibrationOnPage}</span>
          <span className="summary-label">Calibration</span>
        </div>
        <div className="summary-item">
          <span className="summary-value">{visibleSummary.testOnPage}</span>
          <span className="summary-label">Test accounts</span>
        </div>
      </div>

      {/* Bulk action bar */}
      {someSelected && (
        <div className="filters-bar" style={{ background: '#eef2ff', border: '1px solid #c7d2fe' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
            <Tag size={14} />
            <strong>{selectedIds.size}</strong> selected
            <input
              type="text"
              placeholder="Tag (e.g. investor_demo)"
              value={bulkTag}
              onChange={(e) => setBulkTag(e.target.value)}
              style={{ maxWidth: 200, padding: '6px 10px', border: '1px solid #c7d2fe', borderRadius: 6 }}
            />
            <button className="export-btn" onClick={() => handleBulkTag('add')} disabled={!bulkTag.trim()}>
              <Plus size={14} /> Add tag
            </button>
            <button className="export-btn" onClick={() => handleBulkTag('remove')} disabled={!bulkTag.trim()}>
              <Trash2 size={14} /> Remove tag
            </button>
            <button className="refresh-btn" onClick={() => setSelectedIds(new Set())}>Clear</button>
          </div>
        </div>
      )}

      {/* Users Table */}
      <div className="users-table-container">
        {loading ? (
          <div className="loading-state">
            <RefreshCw className="spin" size={32} />
            <span>Loading users...</span>
          </div>
        ) : (
          <>
            <table className="users-table">
              <thead>
                <tr>
                  <th style={{ width: 36 }}>
                    <input type="checkbox" checked={allOnPageSelected} onChange={selectAllOnPage} />
                  </th>
                  <th>User</th>
                  <th>Country</th>
                  <th>Tags / KYC</th>
                  <th>Trust</th>
                  <th>Lifetime</th>
                  <th>Last active</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className={selectedIds.has(user.id) ? 'row-selected' : ''}>
                    <td>
                      <input type="checkbox" checked={selectedIds.has(user.id)} onChange={() => toggleSelect(user.id)} />
                    </td>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar" style={{ background: getRoleColor(user.role) }}>
                          {user.full_name.charAt(0).toUpperCase()}
                        </div>
                        <div className="user-info">
                          <span className="user-name">
                            {user.full_name}
                            {user.is_calibration_user && (
                              <span title="Calibration user" style={{ color: '#410C6D', marginLeft: 6 }}><FlaskConical size={12} /></span>
                            )}
                            {user.is_test_account && (
                              <span title="Test account" style={{ color: '#ef4444', marginLeft: 4 }}><AlertCircle size={12} /></span>
                            )}
                          </span>
                          <span className="user-id">#{user.id} · {user.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      {user.country_code ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <Globe size={12} /> {user.country_code}
                          {user.language_code && <small style={{ color: '#64748b' }}>· {user.language_code}</small>}
                        </span>
                      ) : <small style={{ color: '#94a3b8' }}>—</small>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 }}>
                        {(user.tags || []).slice(0, 4).map((t) => (
                          <span key={t} className="tag-chip">{t}</span>
                        ))}
                        {(user.tags || []).length > 4 && <small style={{ color: '#64748b' }}>+{(user.tags || []).length - 4}</small>}
                      </div>
                      <span className="kyc-pill" data-tier={user.kyc_tier || 'tier_0'}>
                        <BadgeCheck size={11} /> {user.kyc_tier || 'tier_0'}
                      </span>
                    </td>
                    <td>
                      {user.trust_score != null ? (
                        <span style={{ fontWeight: 600 }}>{user.trust_score.toFixed(0)}</span>
                      ) : <small style={{ color: '#94a3b8' }}>—</small>}
                    </td>
                    <td>
                      <div style={{ fontSize: '0.78rem', color: '#475569' }}>
                        <div><Activity size={11} /> {formatNumber(user.lifetime_sessions || 0)} sessions</div>
                        <div><Award size={11} /> {formatCompactNumber(user.lifetime_earnings_tp || 0)} TP</div>
                      </div>
                    </td>
                    <td className="date-cell">
                      {user.last_active_at
                        ? formatDistanceToNow(new Date(user.last_active_at), { addSuffix: true })
                        : <small style={{ color: '#94a3b8' }}>—</small>}
                    </td>
                    <td>
                      <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                        {user.is_active ? <CheckCircle size={14} /> : <XCircle size={14} />}
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button onClick={() => handleViewClick(user)} className="action-btn view" title="View details">
                          <Eye size={16} />
                        </button>
                        <button onClick={() => handleEditClick(user)} className="action-btn edit" title="Edit user">
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() => handleStatusChange(user.id, !user.is_active)}
                          className={`action-btn ${user.is_active ? 'deactivate' : 'activate'}`}
                          title={user.is_active ? 'Deactivate' : 'Activate'}
                        >
                          <Power size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {users.length === 0 && (
              <div className="no-data">
                <UsersIcon size={48} />
                <h3>No users found</h3>
                <p>Try adjusting your search or filters</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Pagination */}
      {users.length > 0 && (
        <div className="pagination">
          <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="page-btn">
            <ChevronLeft size={18} /> Previous
          </button>
          <span className="page-info">
            Page {page + 1} of {Math.max(1, Math.ceil(totalUsers / pageSize))}
            {' · '}{totalUsers} users
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={(page + 1) * pageSize >= totalUsers}
            className="page-btn"
          >
            Next <ChevronRight size={18} />
          </button>
        </div>
      )}

      {/* View User Modal */}
      {viewingUser && (
        <div className="modal-overlay" onClick={handleViewClose}>
          <div className="modal-content view-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 760 }}>
            <div className="modal-header">
              <h3>User Details</h3>
              <button className="modal-close" onClick={handleViewClose}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="user-profile">
                <div className="profile-avatar" style={{ background: getRoleColor(viewingUser.role) }}>
                  {viewingUser.full_name.charAt(0).toUpperCase()}
                </div>
                <div className="profile-info">
                  <h2>{viewingUser.full_name}</h2>
                  <span className={`status-badge ${viewingUser.is_active ? 'active' : 'inactive'}`}>
                    {viewingUser.is_active ? 'Active' : 'Inactive'}
                  </span>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    {(viewingUser.tags || []).map((t) => (
                      <span key={t} className="tag-chip">
                        {t}
                        <button onClick={() => handleRemoveTag(viewingUser.id, t)} title="Remove tag" style={{
                          marginLeft: 4, background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer',
                        }}>
                          <X size={10} />
                        </button>
                      </span>
                    ))}
                    <form onSubmit={(e) => { e.preventDefault(); handleAddTag(viewingUser.id, newTag); }} style={{ display: 'inline-flex', gap: 4 }}>
                      <input
                        type="text"
                        placeholder="add tag"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        style={{ padding: '2px 6px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 11, width: 100 }}
                      />
                      <button type="submit" className="action-btn edit" title="Add tag" disabled={!newTag.trim()}>
                        <Plus size={12} />
                      </button>
                    </form>
                  </div>
                </div>
              </div>

              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label"><Mail size={14} /> Email</span>
                  <span className="detail-value">{viewingUser.email}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><Phone size={14} /> Phone</span>
                  <span className="detail-value">{viewingUser.phone_number}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><Shield size={14} /> Role</span>
                  <span className="detail-value">{viewingUser.role}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><Globe size={14} /> Country</span>
                  <span className="detail-value">{viewingUser.country_code || '—'}{viewingUser.language_code ? ` · ${viewingUser.language_code}` : ''}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><BadgeCheck size={14} /> KYC tier</span>
                  <span className="detail-value">
                    <select value={viewingUser.kyc_tier || 'tier_0'} onChange={(e) => handleSetKyc(viewingUser.id, e.target.value)}
                      style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12 }}>
                      {KYC_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><FlaskConical size={14} /> Calibration</span>
                  <span className="detail-value">{viewingUser.is_calibration_user ? 'Yes' : 'No'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><AlertCircle size={14} /> Test account</span>
                  <span className="detail-value">
                    <label style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="checkbox"
                        checked={!!viewingUser.is_test_account}
                        onChange={(e) => handleToggleTest(viewingUser.id, e.target.checked)}
                      />
                      mark as test
                    </label>
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><Calendar size={14} /> Joined</span>
                  <span className="detail-value">
                    {viewingUser.created_at
                      ? format(new Date(viewingUser.created_at), 'MMM dd, yyyy')
                      : '—'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><Clock size={14} /> Last active</span>
                  <span className="detail-value">
                    {viewingUser.last_active_at
                      ? formatDistanceToNow(new Date(viewingUser.last_active_at), { addSuffix: true })
                      : '—'}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><Activity size={14} /> Trust score</span>
                  <span className="detail-value">{viewingUser.trust_score?.toFixed(1) ?? '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><Award size={14} /> Lifetime sessions</span>
                  <span className="detail-value">{formatNumber(viewingUser.lifetime_sessions || 0)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label"><Wallet size={14} /> Lifetime earnings</span>
                  <span className="detail-value">{formatCompactNumber(viewingUser.lifetime_earnings_tp || 0)} TP</span>
                </div>
                {viewingUser.acquisition_source && (
                  <div className="detail-item">
                    <span className="detail-label">Acquisition</span>
                    <span className="detail-value">
                      {viewingUser.acquisition_source}
                      {viewingUser.acquisition_medium ? ` · ${viewingUser.acquisition_medium}` : ''}
                      {viewingUser.acquisition_campaign ? ` · ${viewingUser.acquisition_campaign}` : ''}
                    </span>
                  </div>
                )}
              </div>

              {/* Timeline */}
              <div className="user-stats-section">
                <h4>Timeline</h4>
                {timeline.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#64748b' }}>No notable events yet.</p>
                ) : (
                  <ul className="timeline">
                    {timeline.map((ev, i) => (
                      <li key={i}>
                        <span className="timeline-dot" data-type={ev.type} />
                        <div>
                          <strong>{ev.label}</strong>
                          {ev.detail && <span className="timeline-detail"> · {ev.detail}</span>}
                          <div className="timeline-time">{format(new Date(ev.occurred_at), 'MMM dd, yyyy HH:mm')}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Notes */}
              <div className="user-stats-section">
                <h4>Admin notes</h4>
                <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a private note about this user (visible only to admins)…"
                    style={{ flex: 1, padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'inherit', fontSize: 13 }}
                    rows={2}
                  />
                  <button className="save-btn" onClick={handleAddNote} disabled={!newNote.trim()}>Add</button>
                </div>
                {notes.length === 0 ? (
                  <p style={{ fontSize: 13, color: '#64748b' }}>No notes yet.</p>
                ) : (
                  <ul className="notes-list">
                    {notes.map((n) => (
                      <li key={n.id}>
                        <div className="note-meta">
                          <strong>{n.admin_name || `Admin #${n.admin_id ?? '?'}`}</strong>
                          <span>{format(new Date(n.created_at), 'MMM dd, yyyy HH:mm')}</span>
                        </div>
                        <p>{n.note}</p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="user-actions-section">
                <h4>Quick Actions</h4>
                <div className="quick-actions">
                  <button onClick={() => handleEditClick(viewingUser)} className="action-btn-lg edit">
                    <Edit2 size={18} /> Edit User
                  </button>
                  <select
                    value={viewingUser.role}
                    onChange={(e) => handleRoleChange(viewingUser.id, e.target.value)}
                    className="role-select"
                  >
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                  <button
                    onClick={() => handleStatusChange(viewingUser.id, !viewingUser.is_active)}
                    className={`action-btn-lg ${viewingUser.is_active ? 'deactivate' : 'activate'}`}
                  >
                    <Power size={18} />
                    {viewingUser.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal-overlay" onClick={handleEditCancel}>
          <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Edit User</h3>
              <button className="modal-close" onClick={handleEditCancel}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Full Name</label>
                <input
                  type="text"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="text"
                  value={editForm.phone_number}
                  onChange={(e) => setEditForm({ ...editForm, phone_number: e.target.value })}
                  className="form-input"
                />
              </div>
              <div className="form-group">
                <label>New Password <span className="optional">(leave blank to keep current)</span></label>
                <input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  className="form-input"
                  placeholder="Enter new password (min 6 characters)"
                />
                {editForm.password && editForm.password.length > 0 && editForm.password.length < 6 && (
                  <span className="form-error">Password must be at least 6 characters</span>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={handleEditCancel} className="cancel-btn">Cancel</button>
              <button onClick={handleEditSave} className="save-btn">Save Changes</button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .tag-chip {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          padding: 2px 8px;
          font-size: 11px;
          font-weight: 600;
          background: #eef2ff;
          color: #4338ca;
          border-radius: 999px;
        }
        .kyc-pill {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 8px;
          font-size: 10px;
          font-weight: 600;
          border-radius: 999px;
          background: #f1f5f9;
          color: #475569;
        }
        .kyc-pill[data-tier="tier_1"] { background: #fef3c7; color: #92400e; }
        .kyc-pill[data-tier="tier_2"] { background: #ddd6fe; color: #5b21b6; }
        .kyc-pill[data-tier="tier_3"] { background: #dcfce7; color: #166534; }
        .row-selected { background: #f8fafc; }

        .timeline {
          list-style: none; padding: 0; margin: 0;
          display: flex; flex-direction: column; gap: 10px;
        }
        .timeline li {
          display: grid; grid-template-columns: 14px 1fr; gap: 10px;
          align-items: flex-start; font-size: 13px; color: #334155;
        }
        .timeline-dot {
          width: 10px; height: 10px; border-radius: 50%;
          background: #410C6D; margin-top: 6px;
          box-shadow: 0 0 0 2px rgba(65,12,109,0.18);
        }
        .timeline-dot[data-type="signup"] { background: #22c55e; box-shadow: 0 0 0 2px rgba(34,197,94,0.18); }
        .timeline-dot[data-type="first_session"] { background: #410C6D; }
        .timeline-dot[data-type="first_payout"] { background: #22c55e; }
        .timeline-dot[data-type="first_win"] { background: #f59e0b; box-shadow: 0 0 0 2px rgba(245,158,11,0.18); }
        .timeline-dot[data-type="fraud_flag"] { background: #ef4444; box-shadow: 0 0 0 2px rgba(239,68,68,0.18); }
        .timeline-dot[data-type="kyc_verified"] { background: #5a1494; }
        .timeline-detail { color: #64748b; }
        .timeline-time { font-size: 11px; color: #94a3b8; margin-top: 2px; }

        .notes-list {
          list-style: none; padding: 0; margin: 0;
          display: flex; flex-direction: column; gap: 8px;
        }
        .notes-list li {
          padding: 10px 12px;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          background: #f8fafc;
        }
        .note-meta {
          display: flex; justify-content: space-between;
          font-size: 12px; color: #64748b;
          margin-bottom: 4px;
        }
        .notes-list p { margin: 0; font-size: 13px; color: #334155; white-space: pre-wrap; }

        :root.dark .tag-chip { background: rgba(99,102,241,0.2); color: #a5b4fc; }
        :root.dark .kyc-pill { background: #334155; color: #cbd5e1; }
        :root.dark .row-selected { background: rgba(99,102,241,0.08); }
        :root.dark .notes-list li { background: #0f172a; border-color: #334155; }
        :root.dark .notes-list p { color: #cbd5e1; }
      `}</style>
    </div>
  );
};

export default Users;
